import {
  Container,
  ElasticContentContainer,
  NewDeviationActionButtons,
  NewDeviationDraft
} from '@cdab/scania/qpr/components'
import { type AuditPoint } from '@cdab/scania/qpr/schema'
import { v4 as uuidv4 } from 'uuid'

import { useDeviation } from '@cdab/scania/qpr/contexts/deviation'
import { useCssVariableBreakpoint } from '@cdab/scania/qpr/hooks'
import type { DeviationModel } from '@cdab/scania/qpr/offline/models'

import { deviationsController } from '@cdab/scania/qpr/offline/controllers'
import { observer } from 'mobx-react-lite'
import { useEffect, useState } from 'react'
import { useLocation, useParams, useRouteLoaderData } from 'react-router-dom'
import invariant from 'tiny-invariant'
import type { AuditLoaderData } from '../../../auditId'

type Params = {
  auditId: string
  auditPointId: string
}

export const AuditDraftDeviationPageNew = observer((): JSX.Element => {
  const { state } = useLocation()

  const { audit } = useRouteLoaderData('audit') as AuditLoaderData
  const { isReadonly, auditPoints } = audit
  const params = useParams<Params>()
  const { auditId: sAuditId, auditPointId: sAuditPointId } = params
  const navigationState = state as { clientGuid?: string } | null
  const deviationId = navigationState?.clientGuid

  const isLg = useCssVariableBreakpoint('--tds-grid-lg')

  const auditId = Number.parseInt(sAuditId ?? 'NaN')
  invariant(auditId, 'Audit not found')

  const INITIAL_DEVIATION_STATE: DeviationModel = {
    auditId,
    auditPointId: -1,
    auditPointNumber: '',
    deviation: '',
    deviationWithoutActions: false,
    expirationDate: null,
    proposedActions: '',
    responsible: '',
    approvalDate: null,
    draft: true,
    files: [],
    clientGuid: deviationId ?? uuidv4(),
    actionPlanId: -1,
    isGettingFiles: !!deviationId
  }

  const [showErrorHelpers, setShowErrorHelpers] = useState(false)
  const [deviationState, setDeviationState] = useState<DeviationModel>(
    deviationId
      ? {
          ...INITIAL_DEVIATION_STATE,
          ...audit.deviations.find(d => d.clientGuid === deviationId)
        }
      : INITIAL_DEVIATION_STATE
  )
  const deviation = audit.deviations.find(
    d => d.clientGuid === deviationState.clientGuid
  )

  const auditPointId = Number.parseInt(sAuditPointId ?? 'NaN')
  let auditPoint: AuditPoint | undefined = undefined
  if (!Number.isNaN(auditPointId)) {
    auditPoint = auditPoints.find(ap => ap.id === auditPointId)
    invariant(auditPoint, `Could not find audit point with id ${auditPointId}`)
  }

  const ctx = useDeviation()

  const onResetDeviation = () => {
    const resetData: DeviationModel = {
      ...INITIAL_DEVIATION_STATE,
      clientGuid: uuidv4()
    }
    setDeviationState(resetData)
    setShowErrorHelpers(false)

    ctx.updateDeviation(resetData, auditPoint)
    if (auditPoint) {
      ctx.form?.setValue('auditPointNumber', auditPoint.auditPointNo)
      ctx.form?.setValue('auditPointId', auditPoint.id.toString())
    }
  }

  useEffect(() => {
    const newDeviationState = deviationId
      ? {
          ...INITIAL_DEVIATION_STATE,
          ...audit.deviations.find(d => d.clientGuid === deviationId)
        }
      : INITIAL_DEVIATION_STATE

    setDeviationState(newDeviationState)
    setShowErrorHelpers(false)

    if (deviationId) {
      deviationsController.GetFiles(audit.id, deviationId)
    }

    ctx.updateDeviation(newDeviationState, auditPoint)
    if (auditPoint) {
      ctx.form?.setValue('auditPointNumber', auditPoint.auditPointNo)
      ctx.form?.setValue('auditPointId', auditPoint.id.toString())
    }
  }, [deviationId, auditId, auditPoint])

  return (
    <Container paddingOnContainer={isLg} fullHeight>
      <ElasticContentContainer
        scrollY
        overflowHidden
        header={
          isLg && (
            <NewDeviationActionButtons
              auditId={auditId}
              existingDeviation={!!deviationId}
              isReadonly={isReadonly}
              deviation={deviationState}
              onResetDeviation={onResetDeviation}
              onShowErrorHelpers={() => setShowErrorHelpers(true)}
            />
          )
        }
        footer={
          !isLg && (
            <NewDeviationActionButtons
              auditId={auditId}
              existingDeviation={!!deviationId}
              isReadonly={isReadonly}
              deviation={deviationState}
              onResetDeviation={onResetDeviation}
              onShowErrorHelpers={() => setShowErrorHelpers(true)}
            />
          )
        }
      >
        <NewDeviationDraft
          isReadOnly={isReadonly}
          auditId={audit.id}
          defaultAuditPoint={auditPoint}
          deviation={deviation ?? deviationState}
          showErrorHelpers={showErrorHelpers}
        />
      </ElasticContentContainer>
    </Container>
  )
})
